import { useBehavior, useButton, useDetectDocumentScroll } from '@libs/hooks';
import { AssetsSvgMap, TAssetsSvgMapValue, TButtonData, TWidgetProps } from '@libs/types';
import clsx from 'clsx';
import React, { Activity, useEffect, useRef, useState } from 'react';
import { SvgComponent } from '../../common/Svg/SvgComponent';
import { TButtonOption } from '../../types/options';
import { getInlineStyles } from '../../utils/styles';
import { TextCommon } from '../UI/Text/TextCommon';
import styles from './styles/buttonCircleAnimated.module.css';

export const ButtonCircleAnimated = ({ widget }: TWidgetProps<TButtonData, TButtonOption>) => {
    const data = widget.data ?? {};
    const options = widget.options ?? {};
    const { handleClick } = useButton(widget);
    const { isDisabled, isHidden } = useBehavior(widget);
    const { documentScrolled } = useDetectDocumentScroll(widget.uid);
    const iconName = options.iconName;
    const icon = AssetsSvgMap[iconName as keyof typeof AssetsSvgMap] ?? 'ic_x';

    const [btnClass, setBtnClass] = useState('');

    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (!isHidden) {
            timeoutRef.current = setTimeout(
                () => {
                    setBtnClass(styles.expandedButton);
                    timeoutRef.current = setTimeout(() => setBtnClass(''), (options.timeToShrink || 30) * 1000);
                },
                (options.timeToExpand || 5) * 1000,
            );
        } else if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [options.timeToShrink, options.timeToExpand, isHidden]);

    const getPosition = () => {
        const dynamicShadow = options.dynamicShadow || true;
        if (dynamicShadow && documentScrolled && !isDisabled) {
            return 'floating';
        } else {
            return options.position;
        }
    };

    const isBoxShadow = () => {
        return getPosition() === 'floating' && !isDisabled;
    };

    return (
        <Activity mode={isHidden ? 'hidden' : 'visible'}>
            <button
                data-test={widget.uid}
                className={clsx(
                    styles.circleAnimatedButton,
                    btnClass,
                    isDisabled && styles.circleAnimatedButtonDisabled,
                    isBoxShadow() && styles.circleAnimatedButtonBoxShadow,
                )}
                style={getInlineStyles({ margin: options.margin })}
                aria-disabled={isDisabled}
                aria-hidden={isHidden}
                aria-label={data.messages?.label}
                onClick={() => handleClick()}
                title={data.messages?.label}
            >
                {data.messages?.label && (
                    <TextCommon
                        isWidget={false}
                        as={'span'}
                        content={data.messages?.label}
                        options={{ variant: 'main', textColor: isDisabled ? 'grey_40_lc' : 'black', margin: { left: 16, right: 40 }, isDisabled: isDisabled }}
                    />
                )}
                {icon && (
                    <div className={styles.iconContainer}>
                        <SvgComponent
                            width={24}
                            height={24}
                            name={icon as TAssetsSvgMapValue}
                            defaultName={AssetsSvgMap.ic_x}
                            fill={isDisabled ? 'grey_40_lc' : 'black'}
                        />
                    </div>
                )}
            </button>
        </Activity>
    );
};
